import { sendTriggerEmails } from "./emailService";
import {
  buildInstallationOrderPdfAttachment,
  buildRemovalOrderPdfAttachment,
  buildSalesOrderPdfAttachment,
} from "./installationOrderPdfAttachment";
import { formatTrackingNumberEmailHtml } from "./publicTrackingUrl";
import {
  fetchPublicInstallationAudienceData,
  fetchPublicRemovalAudienceData,
  fetchPublicOrderAudienceData,
  type PublicOrderAudienceResponse,
} from "./publicOrderAudienceApi";

type SalesTriggerName =
    // "Signage Sales order"
  // "Signage Sales order edit"
  // | "Signage Sales order status update"
  // | "Signage Sales order ready for pickup update"
  // | "Signage Sales order shipped with tracking"
  // | "Signage Sales order shipped manual"
  // | "Agent Sales Order Placed"
  // | "Agent Sales Order Shipped"
  // | "Brokerage Sales order status update"
  // | "Brokerage Sales order"
  // | "Signage Payment applied for order"
   "Public Signage Payment applied for order";

type SendSalesTriggerInput = {
  triggerName: SalesTriggerName;
  orderId: string;
  signageId?: string;
  token: string;
  fromEmail?: string;
  paymentMethodName?: string;
  fallbackUserName?: string;
  fallbackCompanyName?: string;
  includeOrderPdfAttachment?: boolean;
  salesStatusCode?: number;
  extraParams?: Record<string, string>;
};

type InstallationTriggerName =
  // | "Signage Installation order created"
  // "Signage Installation Status change"
  // | "Signage Installation order Canceled"
  "Signage Installation order edit"
  | "Agent Installation order edit"
  | "Brokerage Installation order edit"
  // | "Signage Payment applied for Installation Job"
  | "Brokerage Installation order Created"
  | "Brokerage Installation order Scheduled"
  | "Brokerage Installation order Completed"
  | "Brokerage Installation order Canceled"
  | "Agent Installation Order Created"
  | "Agent Installation Order Scheduled"
  | "Agent Installation Order Completed"
  | "Public Signage Payment applied for Installation Job"
  | "Agent Installation Order Canceled";

type SendInstallationTriggerInput = {
  triggerName: InstallationTriggerName;
  installationOrderId: string;
  token: string;
  fromEmail?: string;
  includeOrderPdfAttachment?: boolean;
  installationStatusCode?: number;
  extraParams?: Record<string, string>;
};

type RemovalTriggerName =
  // | "Signage Removal request created"
  "Brokerage Removal request Created"
  | "Agent Removal Request Created"
  | "Agent Removal Order Scheduled"
  | "Agent Removal Order Completed"
  | "Agent Removal order edit"
  | "Agent Removal Order Canceled"
  | "Agent Removal Order Void"
  | "Brokerage Removal order Scheduled"
  | "Brokerage Removal order Completed"
  | "Brokerage Removal order Canceled"
  | "Brokerage Removal order Void"
  | "Removal order edit"
  | "Removal order Canceled"
  // | "Signage Removal order edit"
  | "Signage Removal order Canceled"
  | "Signage Removal order Voided"
  | "Signage Removal Status change"
  | "Signage Payment applied for removal Job"
  | "Public Signage Payment applied for removal Job";

type SendRemovalTriggerInput = {
  triggerName: RemovalTriggerName;
  removalOrderId: string;
  token: string;
  fromEmail?: string;
  includeOrderPdfAttachment?: boolean;
  extraParams?: Record<string, string>;
};

type AudienceBucket =
  | "ENDUSER"
  | "BROKERAGE"
  | "BROKERAGETEAMMBER"
  | "SIGNAGE"
  | "GROUPEDEMAILS";

const TRIGGER_AUDIENCE_POLICY: Record<SalesTriggerName, AudienceBucket[]> = {
  //  "Signage Sales order": ["ENDUSER", "BROKERAGE", "BROKERAGETEAMMBER", "SIGNAGE", "GROUPEDEMAILS"],
  // ["ENDUSER"],
  // "Signage Sales order edit": ["ENDUSER"],
  // "Signage Sales order status update": ["ENDUSER"],
  // "Signage Sales order ready for pickup update": ["ENDUSER"],
  // "Signage Sales order shipped with tracking": ["ENDUSER"],
  // "Signage Sales order shipped manual": ["ENDUSER"],
  // "Agent Sales Order Placed": ["SIGNAGE","GROUPEDEMAILS"],
  // "Agent Sales Order Shipped": ["SIGNAGE"],
  // "Brokerage Sales order status update": ["SIGNAGE"],
  // "Brokerage Sales order": ["SIGNAGE","GROUPEDEMAILS"],
  // "Signage Payment applied for order": ["ENDUSER", "SIGNAGE", "GROUPEDEMAILS"],
  "Public Signage Payment applied for order": ["ENDUSER", "SIGNAGE", "GROUPEDEMAILS"],
  
  
  // ["ENDUSER", "BROKERAGE", "BROKERAGETEAMMBER", "SIGNAGE", "GROUPEDEMAILS"],
};

const SALES_STATUS_EMAIL_POLICY: Record<number, boolean> = {
  100: false,
  101: false,
  300: false,
  301: true,
  302: true,
  400: true,
  401: false,
};

const INSTALLATION_STATUS_EMAIL_POLICY: Record<number, boolean> = {
  100: false,
  200: false, // Finalized => no email
  300: true,
  400: false, // In Progress => no email
  500: true,
  600: true,
};

const INSTALLATION_TRIGGER_AUDIENCE_POLICY: Record<InstallationTriggerName, AudienceBucket[]> = {
  // "Signage Installation order created": ["ENDUSER","GROUPEDEMAILS"],
  // "Signage Installation Status change": ["ENDUSER"],
  // "Signage Installation order Canceled": ["ENDUSER"],
  "Signage Installation order edit": ["ENDUSER"],
  "Agent Installation order edit": ["BROKERAGE", "SIGNAGE"],
  "Brokerage Installation order edit": ["ENDUSER","SIGNAGE"],
  // "Signage Payment applied for Installation Job": ["ENDUSER", "BROKERAGE", "SIGNAGE", "GROUPEDEMAILS"],
  "Public Signage Payment applied for Installation Job": ["ENDUSER", "BROKERAGE", "SIGNAGE", "GROUPEDEMAILS"],
  "Brokerage Installation order Created": ["SIGNAGE","GROUPEDEMAILS","BROKERAGE"],
  "Brokerage Installation order Scheduled": ["ENDUSER", "SIGNAGE"],
  "Brokerage Installation order Completed": ["ENDUSER","SIGNAGE"],
  "Brokerage Installation order Canceled": ["ENDUSER","SIGNAGE"],
  "Agent Installation Order Created": ["BROKERAGE", "SIGNAGE","GROUPEDEMAILS"],
  "Agent Installation Order Scheduled": [ "BROKERAGE", "SIGNAGE"],
  "Agent Installation Order Completed": [ "BROKERAGE", "SIGNAGE"],
  "Agent Installation Order Canceled": [ "BROKERAGE", "SIGNAGE"],
};

const REMOVAL_TRIGGER_AUDIENCE_POLICY: Record<RemovalTriggerName, AudienceBucket[]> = {
  // "Signage Removal request created": ["ENDUSER","GROUPEDEMAILS"],
  "Brokerage Removal request Created": ["ENDUSER","SIGNAGE","GROUPEDEMAILS"],
  "Agent Removal Request Created": [ "BROKERAGE", "SIGNAGE","GROUPEDEMAILS"],
  "Agent Removal Order Scheduled": [ "BROKERAGE", "SIGNAGE"],
  "Agent Removal Order Completed": [ "BROKERAGE", "SIGNAGE"],
  "Agent Removal order edit": [ "BROKERAGE", "SIGNAGE"],
  "Agent Removal Order Canceled": [ "BROKERAGE", "SIGNAGE"],
  "Agent Removal Order Void": [ "BROKERAGE", "SIGNAGE"],
  "Brokerage Removal order Scheduled": ["ENDUSER","SIGNAGE"],
  "Brokerage Removal order Completed": ["ENDUSER","SIGNAGE"],
  "Brokerage Removal order Canceled": ["ENDUSER","SIGNAGE"],
  "Brokerage Removal order Void": ["ENDUSER","SIGNAGE"],
  "Removal order edit": ["SIGNAGE"],
  "Removal order Canceled": ["SIGNAGE"],
  // "Signage Removal order edit": ["ENDUSER"],
  "Signage Removal order Canceled": ["ENDUSER"],
  "Signage Removal order Voided": ["ENDUSER"],
  "Signage Removal Status change": ["ENDUSER"],
  "Signage Payment applied for removal Job": ["ENDUSER","SIGNAGE", "GROUPEDEMAILS"],
  "Public Signage Payment applied for removal Job": ["ENDUSER","SIGNAGE", "GROUPEDEMAILS"],
};

const formatDate = (value?: string): string => {
  if (!value) return new Date().toLocaleDateString("en-US");
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return new Date().toLocaleDateString("en-US");
  return date.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });
};

const formatPaymentMethodLabel = (method?: string): string => {
  const normalized = String(method || "").trim().toLowerCase();
  if (normalized === "online" || normalized === "card") return "Credit Card";
  if (normalized === "bank_transfer") return "Bank Transfer";
  if (normalized === "cheque") return "Cheque";
  // if (normalized === "store_paid") return "Store Paid";
  return String(method || "").trim() || "N/A";
};

/**
 * Backend provides latest payment on `order_info.payment_info` as a single object:
 * `{ latest_payment_id, payment_amount, payment_date, payment_method, payment_status }`
 * — use as-is (no extra merging / “pick latest” from arrays).
 */
const readOrderInfoPaymentInfo = (orderInfo: any) => {
  const pi = orderInfo?.payment_info;
  if (!pi || typeof pi !== "object" || Array.isArray(pi)) {
    return {
      payment_amount: 0,
      payment_method: "",
      payment_date: "",
      payment_status: "",
      paid_by: null as {
        name?: string;
        email?: string;
        global_user_id?: string;
        membership_id?: string;
        organization_id?: string;
      } | null,
    };
  }
  const rawAmt = (pi as any).payment_amount;
  const n = Number(rawAmt);
  const paidByRaw = (pi as any).paid_by;
  const paidBy =
    paidByRaw && typeof paidByRaw === "object" && !Array.isArray(paidByRaw)
      ? (paidByRaw as {
          name?: string;
          email?: string;
          global_user_id?: string;
          membership_id?: string;
          organization_id?: string;
        })
      : null;
  return {
    payment_amount: Number.isFinite(n) ? n : 0,
    payment_method: String((pi as any).payment_method || "").trim(),
    payment_date: String((pi as any).payment_date || "").trim(),
    payment_status: String((pi as any).payment_status || "").trim(),
    paid_by: paidBy,
  };
};

const formatAddressFromOrderNode = (addr: any): string => {
  if (!addr || typeof addr !== "object") return "";
  return [
    addr?.label,
    addr?.addressLine1,
    addr?.addressLine2,
    addr?.city,
    addr?.state,
    addr?.zip,
    addr?.country,
  ]
    .map((v) => String(v || "").trim())
    .filter(Boolean)
    .join(", ");
};

const pickPersonFullName = (person?: {
  full_name?: string;
  first_name?: string;
  last_name?: string;
} | null): string => {
  const full = String(person?.full_name || "").trim();
  if (full) return full;
  const combined = `${String(person?.first_name || "").trim()} ${String(person?.last_name || "").trim()}`.trim();
  return combined;
};

const resolveGreetingFullName = (
  triggerName: string,
  audience?: PublicOrderAudienceResponse | null,
  fallback = "User",
  groupedEmails: string[] = []
): string => {
  const signageName = pickPersonFullName(audience?.signage_details);
  const brokerageName = pickPersonFullName(audience?.brokerage_details);
  const endUserName = pickPersonFullName(audience?.enduser_details);
  const groupedEmailName = String(groupedEmails[0] || "")
    .trim()
    .split("@")[0]
    .trim();

  if (triggerName.toLowerCase().includes("brokerage")) {
    return brokerageName || endUserName || signageName || groupedEmailName || fallback;
  }
  if (triggerName.toLowerCase().includes("agent")) {
    return endUserName || brokerageName || signageName || groupedEmailName || fallback;
  }
  if (triggerName.toLowerCase().includes("signage")) {
    return signageName || endUserName || brokerageName || groupedEmailName || fallback;
  }
  return endUserName || signageName || brokerageName || groupedEmailName || fallback;
};

/** Keep same allow-list behavior as sendPublicSignagePaymentEmail.ts */
const isEligiblePublicPaymentNotifyMethod = (paymentMethodName: string): boolean => {
  const normalized = String(paymentMethodName || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
  return (
    normalized === "card" ||
    normalized === "online" ||
    normalized === "bank_transfer" ||
    normalized === "cheque"
  );
};

const uniqueEmails = (emails: string[]): string[] => {
  const seen = new Set<string>();
  const result: string[] = [];
  emails.forEach((email) => {
    const normalized = String(email || "").trim().toLowerCase();
    if (!normalized || seen.has(normalized)) return;
    seen.add(normalized);
    result.push(normalized);
  });
  return result;
};

const collectEmailsForRole = (audience?: PublicOrderAudienceResponse | null) => {
  if (!audience) {
    return {
      endUser: [] as string[],
      brokerage: [] as string[],
      brokerageTeammember: [] as string[],
      signage: [] as string[],
      groupedEmails: [] as string[],
    };
  }

  const endUser = uniqueEmails([
    audience.enduser_details?.email || "",
    audience.project_details?.enduser_email || "",
    audience.project_details?.payment_email || "",
  ]);

  const brokerage = uniqueEmails([audience.brokerage_details?.email || ""]);
  const brokerageTeammember = uniqueEmails([audience.brokerage_teammember_details?.email || ""]);

  const extractEmails = (
    entry?:
      | { emails?: string[] }
      | Array<{ emails?: string[] }>
      | null
  ): string[] => {
    if (!entry) return [];
    if (Array.isArray(entry)) return entry.flatMap((item) => item?.emails || []);
    return entry.emails || [];
  };

  const groupedArray = Array.isArray(audience.grouped_details)
    ? audience.grouped_details
    : audience.grouped_details
      ? [audience.grouped_details]
      : [];

  const groupedEmails = uniqueEmails(groupedArray.flatMap((group) => [
    ...extractEmails(group?.license_emails),
    ...extractEmails(group?.licence_emails),
    ...extractEmails(group?.no_licence_emails),
  ]));

  const signage = uniqueEmails([
    audience.signage_details?.email || "",
    audience.signage_teammember_details?.email || "",
  ]);

  return { endUser, brokerage, brokerageTeammember, signage, groupedEmails };
};

const recipientNameFromEmail = (
  email: string,
  audience?: PublicOrderAudienceResponse | null
): string => {
  const target = String(email || "").trim().toLowerCase();
  if (!target) return "User";
  const byPerson = [
    audience?.enduser_details,
    audience?.brokerage_details,
    audience?.signage_details,
    audience?.signage_teammember_details,
    audience?.brokerage_teammember_details,
  ];
  for (const person of byPerson) {
    const personEmail = String(person?.email || "").trim().toLowerCase();
    if (personEmail && personEmail === target) {
      const full = pickPersonFullName(person as any);
      if (full) return full;
    }
  }
  const local = target.split("@")[0]?.trim();
  return local || "User";
};

const withRecipientGreetingParams = (
  baseParams: Record<string, string>,
  recipientName: string
): Record<string, string> => {
  const safeName = String(recipientName || "").trim() || "User";
  const first = safeName.split(/\s+/)[0] || "User";
  return {
    ...baseParams,
    fullName: safeName,
    full_name: safeName,
    "Full Name": safeName,
    firstName: first,
    "First Name": first,
    "Agent First Name": first,
  };
};

const sendTriggerEmailsPerRecipient = async (input: {
  triggerName: string;
  baseParams: Record<string, string>;
  recipients: string[];
  audience?: PublicOrderAudienceResponse | null;
  from: string;
  token: string;
  attachments?: Record<string, string>;
}): Promise<void> => {
  const { triggerName, baseParams, recipients, audience, from, token, attachments } = input;
  for (const email of recipients) {
    const name = recipientNameFromEmail(email, audience);
    const params = withRecipientGreetingParams(baseParams, name);
    await sendTriggerEmails(triggerName, "SIGNAGE_FIRM", params, [email], from, token, attachments);
  }
};

const buildLineItemsHtml = (items: any[]): string => {
  if (!Array.isArray(items) || items.length === 0) {
    return "<p style='color:#6b7280;font-size:14px;margin:0 0 20px 0;'>No items</p>";
  }

  const rows = items
    .map((item) => {
      const name = String(item?.product_name || item?.name || "Product");
      const qty = Number(item?.quantity || 0);
      const description = String(item?.sku || name || "").trim();
      return `
        <tr>
          <td style="padding:12px 0;border-bottom:1px solid #e5e7eb;vertical-align:top;">
            <div style="font-weight:500;color:#111827;font-size:18px;margin-bottom:4px;line-height:22px;">${name}</div>
            <div style="color:#6b7280;font-size:14px;line-height:20px;font-style:italic;">${description}</div>
          </td>
          <td style="padding:12px 0;border-bottom:1px solid #e5e7eb;vertical-align:top;text-align:right;white-space:nowrap;color:#6b7280;font-size:15px;line-height:22px;font-weight:500;">
            Qty: ${qty}
          </td>
        </tr>
      `;
    })
    .join("");

  return `
    <div style="margin:0 0 28px 0;background:#f6f8fb;border-radius:12px;padding:14px;">
      <h3 style="margin:0 0 16px 0;color:#111827;font-size:30px;font-weight:600;font-family:'Roboto',Arial,sans-serif;">Line Items</h3>
      <div style="background-color:#ffffff;border-radius:12px;padding:18px 20px;border:1px solid #d9dee8;">
        <table style="width:100%;border-collapse:collapse;table-layout:fixed;">
          <tbody>${rows}</tbody>
        </table>
      </div>
    </div>
  `;
};

const buildInstallationLineItemsHtml = (items: any[]): string => {
  if (!Array.isArray(items) || items.length === 0) {
    return "<p style='color:#6b7280;font-size:14px;margin:0 0 20px 0;'>No items</p>";
  }

  const rows = items
    .map((item) => {
      const name = String(item?.product_name || item?.name || "Product");
      const qty = Number(item?.quantity || 0);
      const description = String(item?.sku || name || "").trim();
      return `
        <tr>
          <td style="padding:12px 0;border-bottom:1px solid #e5e7eb;vertical-align:top;">
            <div style="font-weight:500;color:#111827;font-size:18px;margin-bottom:4px;line-height:22px;">${name}</div>
            <div style="color:#6b7280;font-size:14px;line-height:20px;font-style:italic;">${description}</div>
          </td>
          <td style="padding:12px 0;border-bottom:1px solid #e5e7eb;vertical-align:top;text-align:right;white-space:nowrap;color:#6b7280;font-size:15px;line-height:22px;font-weight:500;">
            Qty: ${qty}
          </td>
        </tr>
      `;
    })
    .join("");

  return `
    <div style="margin:0 0 28px 0;background:#f6f8fb;border-radius:12px;padding:14px;">
      <div style="background-color:#ffffff;border-radius:12px;padding:18px 20px;border:1px solid #d9dee8;">
        <table style="width:100%;border-collapse:collapse;table-layout:fixed;">
          <tbody>${rows}</tbody>
        </table>
      </div>
    </div>
  `;
};

async function fetchOrderForEmail(orderId: string, token: string): Promise<any | null> {
  const api = String(process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
  if (!api || !orderId) return null;

  const response = await fetch(`${api}/orders/${encodeURIComponent(orderId)}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) return null;
  return response.json();
}

export async function sendSalesOrderTriggerEmail(input: SendSalesTriggerInput): Promise<void> {
  const {
    triggerName,
    orderId,
    signageId,
    token,
    paymentMethodName,
    fallbackUserName,
    fallbackCompanyName,
    includeOrderPdfAttachment,
    salesStatusCode,
    extraParams,
  } = input;
  const from = input.fromEmail || process.env.REACT_APP_FROM_EMAIL2 || "noreply@realesy.com";
  const resolvedTriggerName =
    triggerName === "Public Signage Payment applied for order"
      ? "Signage Payment applied for order"
      : triggerName;
  if (!from || !orderId || !token) return;

  if (
    // triggerName === "Signage Sales order status update" &&
    typeof salesStatusCode === "number" &&
    SALES_STATUS_EMAIL_POLICY[salesStatusCode] === false
  ) {
    return;
  }

  const [orderData, audience] = await Promise.all([
    fetchOrderForEmail(orderId, token),
    fetchPublicOrderAudienceData(signageId, orderId),
  ]);
  if (!orderData) return;

  const orderInfo = (orderData as any)?.order_info || {};
  const payOrder = readOrderInfoPaymentInfo(orderInfo);
  const payAudience = readOrderInfoPaymentInfo((audience as any)?.order_info || {});
  const pay =
    payOrder.payment_method || payOrder.payment_amount
      ? payOrder
      : payAudience;

  if (
    // triggerName === "Signage Payment applied for order" ||
    triggerName === "Public Signage Payment applied for order"
  ) {
    const resolvedPaymentMethod = String(pay.payment_method || paymentMethodName || "").trim();
    if (!isEligiblePublicPaymentNotifyMethod(resolvedPaymentMethod)) {
      return;
    }
  }

  const orderNumber = String(orderData?.order_number || orderId);
  const formattedOrderId = `#${orderNumber}`;
  const orderDate = formatDate(orderData?.created_at);
  const requestedDate = formatDate(orderData?.requested_date || orderData?.order_created_date || orderData?.created_at);
  const statusDate = new Date().toLocaleString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const shipping = orderData?.shipping_address || orderData?.shippingAddress || {};
  const shippingAddress = shipping?.address_details || shipping;
  const propertyAddress = [
    shippingAddress?.addressLine1,
    shippingAddress?.city,
    shippingAddress?.state,
    shippingAddress?.zip,
  ]
    .map((x: any) => String(x || "").trim())
    .filter(Boolean)
    .join(", ");

  const lineItems = Array.isArray(orderData?.items) ? orderData.items : [];
  const productsList = lineItems
    .map((item: any) => `${item?.product_name || item?.name || "N/A"} (Qty: ${item?.quantity || 0})`)
    .join(", ");
  const paymentAmount = `$${Number(pay.payment_amount || 0).toFixed(2)}`;
  const resolvedPaymentMethod = String(pay.payment_method || paymentMethodName || "").trim();
  const formattedPaymentMethod = formatPaymentMethodLabel(resolvedPaymentMethod);
  const paymentDateValue = String(pay.payment_date || "").trim();
  const paymentStatus = String(pay.payment_status || "").trim() || "N/A";
  const paidSoFar = Number((orderData as any)?.amount_paid ?? (orderInfo as any)?.amount_paid ?? 0);
  const totalPaidAfter = Number.isFinite(paidSoFar) ? paidSoFar : 0;
  const remainingBalance = Math.max(0, Number(orderData?.total_amount || 0) - totalPaidAfter);
  const currentBalance = `$${remainingBalance.toFixed(2)}`;
  const trackingNumber = String(orderData?.shipping_method?.tracking_number || "").trim();
  const carrierName = String(
    orderInfo?.carrier_name ||
      orderData?.shipping_method?.carrier_name ||
      orderData?.manual_shipping_method?.name ||
      "N/A"
  ).trim();
  const shippingDate = formatDate(
    orderInfo?.shipping_date ||
      orderData?.shipping_method?.shipping_date ||
      orderData?.updated_at ||
      orderData?.created_at
  );
  const deliveryMethod = trackingNumber ? "Carrier" : "Local Delivery";

  const recipients = collectEmailsForRole(audience);
  const signageName =
    audience?.signage_details?.full_name ||
    `${audience?.signage_details?.first_name || ""} ${audience?.signage_details?.last_name || ""}`.trim() ||
    fallbackCompanyName ||
    "Signage Company";
  const placedBy =
    audience?.enduser_details?.full_name ||
    `${audience?.enduser_details?.first_name || ""} ${audience?.enduser_details?.last_name || ""}`.trim() ||
    fallbackUserName ||
    "User";
  const fullName = resolveGreetingFullName(triggerName, audience, placedBy, recipients.groupedEmails);
  const greetingFirstName = String(fullName || "").trim().split(/\s+/)[0] || "User";
  const agentName =
    String(orderData?.agent_name || "").trim() ||
    `${String(orderData?.customer_first_name || "").trim()} ${String(orderData?.customer_last_name || "").trim()}`.trim() ||
    "N/A";
  const brokerageFirm = String(orderData?.brokerage_firm_name || audience?.brokerage_details?.full_name || "N/A");
  const paidByName = String(pay.paid_by?.name || "").trim();
  const paidByEmail = String(pay.paid_by?.email || "").trim();
  const updatedByStr = String(orderInfo?.updated_by || "").trim();
  const paymentAppliedBy = paidByEmail ? `(${paidByEmail})` : paidByName || updatedByStr || "N/A";
  // const viewOrderUrl = `${String(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}/signage/orderDetails/${encodeURIComponent(orderId)}`;
  const viewOrderUrl = `${String(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}/login`;
  const sellerOrgId = String(
    (orderData as any)?.seller_org_id ||
      (orderData as any)?.signage_id ||
      (orderInfo as any)?.seller_org_id ||
      signageId ||
      ""
  ).trim();

  const params: Record<string, string> = {
    orderId: formattedOrderId,
    fullName,
    full_name: fullName,
    "Full Name": fullName,
    firstName: greetingFirstName,
    ...(sellerOrgId
      ? { seller_org_id: sellerOrgId, organization_id: sellerOrgId, vendor_org_id: sellerOrgId }
      : {}),
    supplierName: audience?.signage_details?.first_name || signageName,
    "Supplier Name": audience?.signage_details?.first_name || signageName,
    "Supplier First Name": audience?.signage_details?.first_name || signageName,
    "First Name": greetingFirstName,
    "Agent First Name": greetingFirstName,
    "Agent Name": placedBy,
    "Agent Full Name": placedBy,
    agentName,
    brokerageFirm,
    paymentAppliedBy,
    paymentAmount,
    paymentMethod: formattedPaymentMethod,
    currentBalance,
    paymentDate: formatDate(paymentDateValue || orderDate),
    paymentStatus,
    viewOrderUrl,
    statusSubject: `Order ${formattedOrderId} status updated`,
    "Signage Admin Name": signageName,
    "Update Date": orderDate,
    "Updated On": orderDate,
    "Changes Made": "Order details updated",
    // "View My Orders": `${String(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}/agent/orders`,
    "View My Orders": `${String(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}/login`,
    "View Updated Order": viewOrderUrl,
    shippedBy: signageName,
    shippedOn: statusDate,
    shippingAddress: propertyAddress || "N/A",
    trackingNumber,
    trackingNumberHtml: formatTrackingNumberEmailHtml(trackingNumber),
    carrierName: carrierName || "N/A",
    deliveryMethod,
    vendorName: signageName,
    readySince: statusDate,
    "Order ID": orderNumber,
    "Order Number": orderNumber,
    orderNumber,
    orderDate,
    "MM/DD/YYYY": orderDate,
    requestedDate,
    "Install Date": requestedDate,
    "Requested Date": requestedDate,
    placedBy,
    submittedBy: placedBy,
    "Signage Company Name": signageName,
    signageCompanyName: signageName,
    brokerageFirmName: audience?.brokerage_details?.full_name || "Brokerage",
    "Property or Office Address": propertyAddress || "N/A",
    "Install Location": propertyAddress || "N/A",
    dropOffLocation: propertyAddress || "N/A",
    deliveryLocation: propertyAddress || "N/A",
    productsOrdered: productsList || "N/A",
    "Product list or summary": productsList || "N/A",
    productsRequired: productsList || "N/A",
    lineItems: buildLineItemsHtml(lineItems),
    ...(extraParams || {}),
  };

  // Audience routing is policy-driven (similar to dashboard trigger mapping).
  const policy = TRIGGER_AUDIENCE_POLICY[triggerName] || [];
  const mergedRecipients = uniqueEmails([
    ...(policy.includes("ENDUSER") ? recipients.endUser : []),
    ...(policy.includes("BROKERAGE") ? recipients.brokerage : []),
    ...(policy.includes("BROKERAGETEAMMBER") ? recipients.brokerageTeammember : []),
    ...(policy.includes("SIGNAGE") ? recipients.signage : []),
    ...(policy.includes("GROUPEDEMAILS") ? recipients.groupedEmails : []),
  ]);

  if (!mergedRecipients.length) return;

  let attachments: Record<string, string> | undefined;
  if (includeOrderPdfAttachment) {
    try {
      const pdfSafeName = String(orderNumber || orderId)
        .replace(/#/g, "")
        .replace(/[^\w.-]+/g, "_");
      attachments = await buildSalesOrderPdfAttachment({
        salesOrderId: String(orderId).trim(),
        token,
        fileLabel: pdfSafeName,
        pdfFetch: {
          maxAttempts: 6,
          initialDelayMs: 400,
          retryDelayMs: 500,
          perFetchTimeoutMs: 15_000,
        },
      });
    } catch {
      // Keep mail flow resilient if PDF generation fails.
      attachments = undefined;
    }
  }

  await sendTriggerEmailsPerRecipient({
    triggerName: resolvedTriggerName,
    baseParams: params,
    recipients: mergedRecipients,
    audience,
    from,
    token,
    attachments,
  });
}

export async function sendInstallationOrderTriggerEmail(
  input: SendInstallationTriggerInput
): Promise<void> {
  const {
    triggerName,
    installationOrderId,
    token,
    includeOrderPdfAttachment,
    installationStatusCode,
    extraParams,
  } = input;
  const from = input.fromEmail || process.env.REACT_APP_FROM_EMAIL2 || "noreply@realesy.com";
  if (!from || !installationOrderId || !token) return;

  // if (
  //   triggerName === "Signage Installation Status change" &&
  //   typeof installationStatusCode === "number" &&
  //   INSTALLATION_STATUS_EMAIL_POLICY[installationStatusCode] === false
  // ) {
  //   return;
  // }

  const audience = await fetchPublicInstallationAudienceData(installationOrderId);
  if (!audience) return;

  const orderInfo = (audience as any)?.order_info || {};
  const orderNumber = String(orderInfo?.order_number || installationOrderId).trim();
  const formattedOrderId = `#${orderNumber}`;
  const propertyAddress = String(orderInfo?.property_address || "").trim() || "N/A";
  const scheduledDate = formatDate(orderInfo?.scheduled_date || orderInfo?.requested_date || orderInfo?.order_created_date);
  const updatedOn = formatDate(orderInfo?.updated_date || new Date().toISOString());
  const submittedOn = formatDate(orderInfo?.order_created_date || orderInfo?.updated_date);
  const newStatus = String(orderInfo?.status || "").trim() || "Updated";
  const updateTime = orderInfo?.updated_date
    ? new Date(orderInfo.updated_date).toLocaleString("en-US")
    : new Date().toLocaleString("en-US");
  const signageCompanyName =
    audience?.signage_details?.full_name ||
    `${audience?.signage_details?.first_name || ""} ${audience?.signage_details?.last_name || ""}`.trim() ||
    "Signage Company";
  const endUserName =
    audience?.enduser_details?.full_name ||
    `${audience?.enduser_details?.first_name || ""} ${audience?.enduser_details?.last_name || ""}`.trim() ||
    String((audience as any)?.project_details?.enduser_name || "").trim() ||
    "Customer";
  const agentName =
    String(orderInfo?.ship_to || "").trim() ||
    endUserName ||
    "N/A";
  const brokerageFirm = audience?.brokerage_details?.full_name || "N/A";
  const supplierName = pickPersonFullName(audience?.signage_details) || signageCompanyName;
  const brokerageFirmName =
    String((audience as any)?.brokerage_details?.firm_name || "").trim() ||
    String((audience as any)?.brokerage_details?.full_name || "").trim() ||
    brokerageFirm;
  const installLocation =
    formatAddressFromOrderNode(orderInfo?.shipping_address) ||
    propertyAddress;
  const photosProvidedRaw = (orderInfo as any)?.proof_upload;
  const photosProvided = Array.isArray(photosProvidedRaw)
    ? photosProvidedRaw.length > 0
      ? photosProvidedRaw.join(", ")
      : "No"
    : String(photosProvidedRaw || "").trim() || "No";
  const notesText = String((orderInfo as any)?.notes || (orderInfo as any)?.additional_instructions || "").trim() || "N/A";
  const canceledOn = formatDate((orderInfo as any)?.cancel_date || (orderInfo as any)?.canceled_on || "");
  const canceledBy = String((orderInfo as any)?.cancel_by || (orderInfo as any)?.canceled_by || orderInfo?.updated_by || "").trim() || "N/A";

  const items = Array.isArray(orderInfo?.line_items) ? orderInfo.line_items : [];
  const p = readOrderInfoPaymentInfo(orderInfo);
  const paymentAmount = `$${Number(p.payment_amount || 0).toFixed(2)}`;
  const paymentMethod = formatPaymentMethodLabel(p.payment_method);
  const paymentDate = formatDate(p.payment_date || orderInfo?.updated_date);
  const paymentStatus = String(p.payment_status || "").trim() || "N/A";
  const paymentAppliedBy = String(orderInfo?.updated_by || "").trim() || signageCompanyName;

  const recipients = collectEmailsForRole(audience);
  const fullName = resolveGreetingFullName(triggerName, audience, endUserName, recipients.groupedEmails);
  const greetingFirstName = String(fullName || "").trim().split(/\s+/)[0] || "User";
  const policy = INSTALLATION_TRIGGER_AUDIENCE_POLICY[triggerName] || [];
  const mergedRecipients = uniqueEmails([
    ...(policy.includes("ENDUSER") ? recipients.endUser : []),
    ...(policy.includes("BROKERAGE") ? recipients.brokerage : []),
    ...(policy.includes("BROKERAGETEAMMBER") ? recipients.brokerageTeammember : []),
    ...(policy.includes("SIGNAGE") ? recipients.signage : []),
    ...(policy.includes("GROUPEDEMAILS") ? recipients.groupedEmails : []),
  ]);
  if (!mergedRecipients.length) return;

  let attachments: Record<string, string> | undefined;
  const shouldAttachInstallationPdf =
    includeOrderPdfAttachment 
    // triggerName === "Signage Installation order created" ||
    // triggerName === "Signage Payment applied for Installation Job";
  if (shouldAttachInstallationPdf) {
    try {
      const safeName = String(orderNumber || installationOrderId).replace(/[^\w.-]+/g, "_");
      attachments = await buildInstallationOrderPdfAttachment({
        installationOrderId,
        token,
        fileLabel: safeName,
        pdfFetch: {
          maxAttempts: 6,
          initialDelayMs: 400,
          retryDelayMs: 500,
          perFetchTimeoutMs: 15_000,
        },
      });
    } catch {
      attachments = undefined;
    }
  }

  const baseUrl = String(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "");
  // const viewUpdatedOrder = `${baseUrl}/signage/jobs/installationWorkOrder/${encodeURIComponent(installationOrderId)}`;
  const viewUpdatedOrder = `${baseUrl}/login`;
  const sellerOrgId = String(
    (orderInfo as any)?.seller_org_id ||
      (orderInfo as any)?.signage_id ||
      (audience as any)?.signage_details?.organization_id ||
      ""
  ).trim();
  const params: Record<string, string> = {
    fullName,
    full_name: fullName,
    "Full Name": fullName,
    firstName: greetingFirstName,
    customerName: endUserName,
    orderId: formattedOrderId,
    orderNumber,
    ...(sellerOrgId
      ? { seller_org_id: sellerOrgId, organization_id: sellerOrgId, vendor_org_id: sellerOrgId }
      : {}),
    supplierName,
    signageCompanyName: supplierName,
    "Signage Company Name": supplierName,
    propertyAddress,
    scheduledDate,
    requestedInstallDate: scheduledDate,
    "Requested Install Date": scheduledDate,
    "Submitted On": submittedOn,
    submittedOn,
    agentName,
    brokerageFirm,
    brokerageFirmName,
    newStatus,
    updatedBy: String(orderInfo?.updated_by || signageCompanyName),
    updateTime,
    reason: String(orderInfo?.changes_made || "Not provided"),
    lineItems: buildInstallationLineItemsHtml(items),
    paymentAppliedBy,
    paymentAmount,
    paymentMethod,
    paymentDate,
    paymentStatus,
    "First Name": greetingFirstName,
    "Order ID": formattedOrderId,
    "Order Number": formattedOrderId,
    "Edited On": updatedOn,
    "Updated On": updatedOn,
    "Signage Team Member Name": String(orderInfo?.updated_by || signageCompanyName),
    "Changes Made": String(orderInfo?.changes_made || "Order details updated"),
    "Install Location": installLocation || propertyAddress,
    installationAddress: installLocation || propertyAddress,
    "Photos Provided": photosProvided,
    photosProvided,
    "Sign placed near front gate": notesText,
    notes: notesText,
    "Canceled On": canceledOn,
    canceledOn,
    "Canceled By": canceledBy,
    canceledBy,
    "Agent Name": agentName,
    "View Updated Order": viewUpdatedOrder,
    ...(extraParams || {}),
  };

  await sendTriggerEmailsPerRecipient({
    triggerName,
    baseParams: params,
    recipients: mergedRecipients,
    audience,
    from,
    token,
    attachments,
  });
}

export async function sendRemovalOrderTriggerEmail(
  input: SendRemovalTriggerInput
): Promise<void> {
  const {
    triggerName,
    removalOrderId,
    token,
    includeOrderPdfAttachment,
    extraParams,
  } = input;
  const resolvedTriggerName =
    triggerName === "Public Signage Payment applied for removal Job"
      ? "Signage Payment applied for removal Job"
      : triggerName;
  const from = input.fromEmail || process.env.REACT_APP_FROM_EMAIL2 || "noreply@realesy.com";
  if (!from || !removalOrderId || !token) return;

  const audience = await fetchPublicRemovalAudienceData(removalOrderId);
  if (!audience) return;

  const orderInfo = (audience as any)?.order_info || {};
  const orderNumber = String(orderInfo?.order_number || removalOrderId).trim();
  const orderIdLabel = `#${orderNumber}`;
  const submittedOn = formatDate(orderInfo?.order_created_date || orderInfo?.updated_date);
  const signageCompanyName =
    audience?.signage_details?.full_name ||
    `${audience?.signage_details?.first_name || ""} ${audience?.signage_details?.last_name || ""}`.trim() ||
    "Signage Company";
  const customerName =
    audience?.enduser_details?.full_name ||
    String(orderInfo?.ship_to || "").trim() ||
    "Customer";
  const supplierName = pickPersonFullName(audience?.signage_details) || signageCompanyName;
  const brokerageFirmName =
    String((audience as any)?.brokerage_details?.firm_name || "").trim() ||
    String((audience as any)?.brokerage_details?.full_name || "").trim() ||
    "N/A";
  const submittedBy =
    String(orderInfo?.updated_by || "").trim() ||
    customerName;
  const p = readOrderInfoPaymentInfo(orderInfo);
  const paymentAmount = `$${Number(p.payment_amount || 0).toFixed(2)}`;
  const paymentMethod = formatPaymentMethodLabel(p.payment_method);
  const paymentDate = formatDate(p.payment_date || orderInfo?.updated_date);
  const paymentStatus = String(p.payment_status || "").trim() || "N/A";
  const paymentAppliedBy = String(orderInfo?.updated_by || "").trim() || signageCompanyName;
  const location = String(orderInfo?.property_address || "").trim() || "N/A";
  const installLocation = formatAddressFromOrderNode(orderInfo?.shipping_address) || location;
  const requestedInstallDate = formatDate(orderInfo?.scheduled_date || orderInfo?.requested_date || orderInfo?.order_created_date);
  const photosProvidedRaw = (orderInfo as any)?.proof_upload;
  const photosProvided = Array.isArray(photosProvidedRaw)
    ? photosProvidedRaw.length > 0
      ? photosProvidedRaw.join(", ")
      : "No"
    : String(photosProvidedRaw || "").trim() || "No";
  const notesText = String((orderInfo as any)?.notes || (orderInfo as any)?.additional_instructions || "").trim() || "N/A";
  const canceledOn = formatDate((orderInfo as any)?.cancel_date || (orderInfo as any)?.canceled_on || "");
  const canceledBy = String((orderInfo as any)?.cancel_by || (orderInfo as any)?.canceled_by || orderInfo?.updated_by || "").trim() || "N/A";
  const baseUrl = (process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "");
      // const viewOrderUrl = `${baseUrl}/signage/jobs/viewRemovalRequest/${encodeURIComponent(removalOrderId)}`;
  const viewOrderUrl = `${baseUrl}/login`;

  const recipients = collectEmailsForRole(audience);
  const fullName = resolveGreetingFullName(triggerName, audience, customerName, recipients.groupedEmails);
  const firstName = String(fullName || "").trim().split(/\s+/)[0] || "Customer";
  const sellerOrgId = String(
    (orderInfo as any)?.seller_org_id ||
      (orderInfo as any)?.signage_id ||
      (audience as any)?.signage_details?.organization_id ||
      ""
  ).trim();

  const params: Record<string, string> = {
    orderId: orderIdLabel,
    orderNumber,
    ...(sellerOrgId
      ? { seller_org_id: sellerOrgId, organization_id: sellerOrgId, vendor_org_id: sellerOrgId }
      : {}),
    supplierName,
    brokerageFirmName,
    fullName,
    full_name: fullName,
    "Full Name": fullName,
    firstName,
    "First Name": firstName,
    customerName,
    signageCompanyName: supplierName,
    "Signage Company Name": supplierName,
    location,
    "Install Location": installLocation || location,
    requestedInstallDate,
    "Requested Install Date": requestedInstallDate,
    "Photos Provided": photosProvided,
    photosProvided,
    "Sign placed near front gate": notesText,
    notes: notesText,
    submittedOn,
    "Submitted On": submittedOn,
    submittedBy,
    "Canceled On": canceledOn,
    canceledOn,
    "Canceled By": canceledBy,
    canceledBy,
    paymentAppliedBy,
    paymentAmount,
    paymentMethod,
    paymentDate,
    paymentStatus,
    viewOrderUrl,
    ...(extraParams || {}),
  };

  const policy = REMOVAL_TRIGGER_AUDIENCE_POLICY[triggerName] || [];
  const mergedRecipients = uniqueEmails([
    ...(policy.includes("ENDUSER") ? recipients.endUser : []),
    ...(policy.includes("BROKERAGE") ? recipients.brokerage : []),
    ...(policy.includes("BROKERAGETEAMMBER") ? recipients.brokerageTeammember : []),
    ...(policy.includes("SIGNAGE") ? recipients.signage : []),
    ...(policy.includes("GROUPEDEMAILS") ? recipients.groupedEmails : []),
  ]);
  if (!mergedRecipients.length) return;

  let attachments: Record<string, string> | undefined;
  if (includeOrderPdfAttachment) {
    try {
      attachments = await buildRemovalOrderPdfAttachment({
        removalOrderId,
        token,
        fileLabel: orderIdLabel,
      });
    } catch {
      attachments = undefined;
    }
  }

  await sendTriggerEmailsPerRecipient({
    triggerName: resolvedTriggerName,
    baseParams: params,
    recipients: mergedRecipients,
    audience,
    from,
    token,
    attachments,
  });
}
